import { useEffect, useRef } from "react";

/**
 * Adaptive polling hook.
 *
 * Replaces a plain setInterval with a smarter scheduler that:
 *  - Chains setTimeout calls so a slow request can never cause overlapping fires.
 *  - Pauses automatically while the tab is hidden and resumes immediately on
 *    visibility or window focus.
 *  - Backs off exponentially (capped at maxInterval) on consecutive errors so
 *    a flaky server doesn't generate a request storm.
 *
 * @param {() => Promise<void>} callback  Async function to call on each tick.
 *   Errors thrown by callback increment the backoff counter; if the callback
 *   silently handles its own errors (the common pattern here), pass the raw
 *   sync function — backoff will never engage, which is fine.
 *
 * @param {object}  opts
 * @param {number}  [opts.baseInterval=5000]     Normal interval in ms.
 * @param {number}  [opts.maxInterval=30000]     Cap for backoff in ms.
 * @param {number}  [opts.backoffFactor=1.8]     Multiplier per consecutive error.
 * @param {boolean} [opts.enabled=true]          Set false to suspend polling.
 */
export function useAdaptivePolling(callback, {
  baseInterval = 5000,
  maxInterval = 30000,
  backoffFactor = 1.8,
  enabled = true,
} = {}) {
  // Keep a stable ref so we never need to re-run the effect when the callback
  // identity changes (avoids the timer being torn down and restarted on every render).
  const callbackRef = useRef(callback);
  useEffect(() => { callbackRef.current = callback; });

  useEffect(() => {
    if (!enabled) return undefined;

    let timeoutId = null;
    let consecutiveErrors = 0;
    let active = true;

    const nextDelay = () =>
      Math.min(
        baseInterval * Math.pow(backoffFactor, consecutiveErrors),
        maxInterval
      );

    const schedule = () => {
      if (!active) return;
      // Don't schedule while hidden — onVisible will trigger instead.
      if (document.visibilityState === "hidden") return;
      timeoutId = setTimeout(tick, nextDelay());
    };

    const tick = async () => {
      if (!active) return;
      try {
        await callbackRef.current();
        consecutiveErrors = 0;
      } catch (_) {
        consecutiveErrors = Math.min(consecutiveErrors + 1, 5);
      }
      schedule();
    };

    const onVisible = () => {
      if (!active || document.visibilityState !== "visible") return;
      clearTimeout(timeoutId);
      tick();
    };

    const onFocus = () => {
      if (!active) return;
      clearTimeout(timeoutId);
      tick();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);

    // Kick off immediately on mount.
    tick();

    return () => {
      active = false;
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, baseInterval, maxInterval, backoffFactor]);
}
